import React, { useEffect } from 'react';

import { StepProps, track, useAutoNavigate } from '@betterment/js-runtime';
import { Icon } from '@betterment/samba-react';
import { Box, Flex, Text } from '@betterment/style-closet-react';

import { CustomerSignupValues } from './Controller';

import { useCustomerResponse } from '../Checking/components/CustomerResponseProvider';
import SignupPending from '../Checking/components/SignupPending';
import { isSignedUp } from '../Checking/models/customer';
import { gtmTrack } from '../Shared/google-tag-manager-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

const SUCCESS_MESSAGE_DISPLAY_DURATION = 2000;

interface SignupSuccessfulProps {
  isSecondaryUser: boolean;
}

const SignupSuccessful: React.FC<React.PropsWithChildren<SignupSuccessfulProps>> = ({ isSecondaryUser }) => {
  return (
    <Box>
      <Box display="flex" height="100vh">
        <Box position="relative" width="100%">
          <Flex
            className="LoadingSpinner-background"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Box height={48} mb={8}>
              <Icon name="CheckmarkCircle" size="medium" color="icon.success" />
            </Box>
            <Box>
              <Text color="bmtGrey90" lineHeight="4">
                {isSecondaryUser && 'Joint '}Checking account created
              </Text>
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

type Props = StepProps<CustomerSignupValues>;

const Success: React.FC<React.PropsWithChildren<Props>> = ({ flowState, goToNextStep }) => {
  const { customer, loading: isCustomerLoading, refetch: reloadCustomer } = useCustomerResponse();
  const isWaiting = isCustomerLoading || (customer && !isSignedUp(customer)) || !customer;
  const navigate = useAutoNavigate();
  const { accountType, invitationId } = flowState.values;
  const isSecondaryUser = invitationId !== undefined;

  useEffect(() => {
    gtmTrack('checkingAccountCreated');
    track('ElementViewed', { name: 'BettermentAccountApproved', flow: 'OpenCheckingAccount' });
  }, []);

  useEffect(() => {
    if (!isWaiting && customer) {
      setTimeout(() => {
        if (accountType === 'joint') {
          void navigate(urlBuilder.getAppCheckingJointAccounts.absolute({ accountId: customer.accounts[0].id }));
        } else {
          goToNextStep({ customer, accountId: customer.accounts[0].id });
        }
      }, SUCCESS_MESSAGE_DISPLAY_DURATION);
    }
  }, [isWaiting, customer, accountType, goToNextStep, navigate]);

  const signupPendingMessage = isSecondaryUser
    ? 'Creating your Joint Checking account...'
    : 'Creating your Checking account...';

  return isWaiting ? (
    <Box>
      <SignupPending message={signupPendingMessage} reloadCustomer={reloadCustomer} />
    </Box>
  ) : (
    <SignupSuccessful isSecondaryUser={isSecondaryUser} />
  );
};

export default Success;
